import * as THREE from 'three/webgpu';
import { Ambience } from '../engine/audio/Ambience';
import { HeroModel } from '../engine/character/HeroModel';
import { PlayerController } from '../engine/character/PlayerController';
import { ShoulderCamera } from '../engine/character/ShoulderCamera';
import type { Capabilities } from '../engine/core/capabilities';
import type { Engine } from '../engine/core/Engine';
import { flags } from '../engine/core/flags';
import { quality, saveSettings, settings, touchDevice } from '../engine/core/settings';
import { DebugUI } from '../engine/debug/DebugUI';
import { saveScreenshot } from '../engine/debug/photo';
import { Stats } from '../engine/debug/Stats';
import { PhysicsWorld } from '../engine/physics/PhysicsWorld';
import { AsyncPipelines } from '../engine/render/AsyncPipelines';
import { applyRendererSize, dynamicResolution } from '../engine/render/createRenderer';
import { DynamicResolution } from '../engine/render/DynamicResolution';
import { PostPipeline, SceneSurface } from '../engine/render/PostPipeline';
import { RenderOrigin } from '../engine/render/RenderOrigin';
import { ShaderWarmup } from '../engine/render/ShaderWarmup';
import { Hud } from '../engine/ui/Hud';
import { screens } from '../engine/ui/screens';
import { TouchControls } from '../engine/ui/TouchControls';
import { CombatTest } from './combat/CombatTest';
import { buildUniverse } from './custom/build';
import { PREFAB_IDS, PREFABS, type PrefabId } from './custom/prefabs';
import { loadUniverse, type UniverseSpec } from './custom/spec';
import { buildDebugPanel, restoreBookmark } from './dev/debugPanel';
import { THREE_WORLDS, VERDANT_TO_GLOWMIRE } from './hollows/caves';
import { EMBERDEEP } from './hollows/emberdeep';
import { GEODE_HOLLOWS, GEODES } from './hollows/geodes';
import { GLOWMIRE } from './hollows/glowmire';
import { HALO } from './hollows/halo';
import { TITAN } from './hollows/titan';
import { UMBRAL } from './hollows/umbral';
import { VERDANT } from './hollows/verdant';
import { Stones } from './props/Stones';
import { CaveLab } from './scenes/CaveLab';
import { type CaveDef, CaveWorld } from './scenes/CaveWorld';
import type { GameWorld } from './scenes/GameWorld';
import { HollowScene, type HollowSceneDef, LANTERN_COLOR, LANTERN_INTENSITY } from './scenes/HollowScene';
import { SandboxScene } from './scenes/SandboxScene';
import { markShadersReady } from './ui/shadersReady';
import { setupStartScreen, showHintsOnce } from './ui/startScreen';

/** The world behind the menu when no world is named in the URL: Nexus, the default world. */
export const MENU_WORLD: PrefabId = 'nexus';

/** Terrain hollows by ?scene= name (the geodes one at a time are for testing). */
const HOLLOWS: Record<string, HollowSceneDef> = {
  verdant: VERDANT,
  glowmire: GLOWMIRE,
  emberdeep: EMBERDEEP,
  umbral: UMBRAL,
  titan: TITAN,
  halo: HALO,
  ...GEODE_HOLLOWS,
};

/** Hollows joined by caves, by ?scene= name. */
const CAVES: Record<string, CaveDef> = {
  cave: VERDANT_TO_GLOWMIRE,
  journey: THREE_WORLDS,
  geodes: GEODES,
};

const isPrefab = (name: string): name is PrefabId => (PREFAB_IDS as readonly string[]).includes(name);

/**
 * A universe made of the editor's worlds: the custom one (`?scene=custom`: the one in the
 * link, `&w=`, else the one saved by the editor), or a prefab by its id (`?scene=nexus`, the
 * default world). One world plays on its own; more are joined by caves.
 */
function universeWorld(
  u: UniverseSpec,
  origin: RenderOrigin,
  scene: THREE.Scene,
  renderer: THREE.WebGPURenderer,
  hints?: string[],
): GameWorld {
  const built = buildUniverse(u);
  for (const note of built.notes) console.info('[innerworlds] custom:', note);
  if (!built.cave) {
    if (hints) built.hollows[0].hints = hints;
    return new HollowScene(built.hollows[0], origin, scene, renderer);
  }
  if (hints) built.cave.hints = hints;
  return new CaveWorld(built.cave, origin, scene, renderer);
}

const _eye = new THREE.Vector3();
/** Where the camera is (universe): the eye, or over the shoulder in third person. */
const _cam = new THREE.Vector3();
const _viewQ = new THREE.Quaternion();
const _fwd = new THREE.Vector3();
const _gView = new THREE.Vector3();
const _invQ = new THREE.Quaternion();
const _tmp = new THREE.Vector3();
const _attractQ = new THREE.Quaternion();
const _attractQ2 = new THREE.Quaternion();
const _X = new THREE.Vector3(1, 0, 0);
const _Y = new THREE.Vector3(0, 1, 0);

/**
 * Load `sceneName` and run it. The loading bar is approximate: each stage gets the share of
 * the bar it usually takes on a first visit, when compiling the shaders dominates.
 */
export async function startGame(engine: Engine, caps: Capabilities, sceneName: string): Promise<void> {
  const { renderer } = engine;
  // Shaders compile in the background: nothing ever waits for one (see AsyncPipelines).
  const pipelines = new AsyncPipelines(renderer);
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x05070a);
  const camera = new THREE.PerspectiveCamera(settings.fov, 1, 0.05, 5e7);
  const origin = new RenderOrigin();

  const world: GameWorld =
    sceneName === 'sandbox'
      ? new SandboxScene(origin, scene)
      : sceneName === 'cavelab'
        ? new CaveLab(origin, scene)
        : sceneName === 'custom'
          ? universeWorld(loadUniverse(), origin, scene, renderer)
          : isPrefab(sceneName)
            ? universeWorld(PREFABS[sceneName].make(), origin, scene, renderer, PREFABS[sceneName].hints)
            : CAVES[sceneName]
              ? new CaveWorld(CAVES[sceneName], origin, scene, renderer)
              : new HollowScene(HOLLOWS[sceneName] ?? VERDANT, origin, scene, renderer);
  const t0 = performance.now();
  const since = () => `${((performance.now() - t0) / 1000).toFixed(1)} s`;
  screens.bootStatus(`Shaping ${world.name}…`, 0.15);
  await world.prepare?.();
  console.info('[innerworlds] boot: world prepared', since());
  const physics = await PhysicsWorld.create(world.field, world.hollows[0].center);
  world.initPhysics(physics);
  const player = new PlayerController(physics, world.field, engine.input);
  screens.bootStatus('Finding a place to stand…', 0.25);
  await world.spawn(player);
  console.info('[innerworlds] boot: spawned', since());
  restoreBookmark(player);
  // ?time=18 → local clock time (hours) at the start position.
  const hour = flags.num('time', Number.NaN);
  const home = world.hollowAt(player.position);
  if (world.rig && home && Number.isFinite(hour)) {
    world.rig.setLocalHour(_tmp.subVectors(player.position, home.center).normalize(), hour);
  }
  const stones = new Stones(physics, origin, scene);
  // The combat prototype: in the gravity sandbox, or anywhere with ?combat=1.
  const combat = flags.bool('combat', world instanceof SandboxScene)
    ? new CombatTest(engine, player, physics, world.field, origin, scene)
    : null;
  // Third person: the camera over the hero's shoulder (O, or ?third=1). The hero is in the
  // scene from the start, so the boot's shader warm-up compiles it too.
  const hero = new HeroModel(LANTERN_COLOR, LANTERN_INTENSITY);
  scene.add(hero.group);
  combat?.holdBy(hero);
  const shoulder = new ShoulderCamera(physics, player.collider);
  shoulder.enabled = flags.bool('third', settings.thirdPerson);

  // Every world gets the full post chain (TRAA, bloom, grade); aerial perspective and
  // clouds only where the world has them.
  // Light volumes of glowing plants and crystals get their own (deferred) pass.
  const lightScene = new THREE.Scene();
  for (const g of world.lightGroups ?? []) lightScene.add(g);
  let hasLights = false;
  lightScene.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) hasLights = true;
  });
  // The scene is rendered once, into one set of passes; a world made of several hollows
  // (joined by caves) puts one post chain per sky on top of it.
  const traaOn = !flags.bool('noaa');
  const surface = new SceneSurface(scene, camera, {
    ao: quality().ao && flags.bool('ao', true),
    traa: traaOn,
    lights: hasLights && flags.bool('lights', true) ? lightScene : null,
  });
  const posts = new Map<unknown, PostPipeline>();
  const postFor = (atmosphere: GameWorld['atmosphere'], clouds: GameWorld['clouds'] | null): PostPipeline => {
    let p = posts.get(atmosphere ?? null);
    if (!p) {
      p = new PostPipeline(renderer, surface, camera, {
        atmosphere: atmosphere ?? null,
        clouds: clouds ?? null,
        bloom: quality().bloom,
        traa: traaOn,
        // ?seams=1: pixels nothing was drawn to show in magenta.
        seams: flags.bool('seams'),
        // ?sharpen=0 turns it off.
        sharpen: flags.num('sharpen', 0.5),
      });
      posts.set(atmosphere ?? null, p);
    }
    return p;
  };
  postFor(world.atmosphere, world.clouds);
  for (const s of world.skies ?? []) postFor(s.atmosphere, s.clouds);

  const stats = new Stats(renderer);
  stats.setVisible(settings.showStats);
  const hud = new Hud();
  const debug = new DebugUI();
  buildDebugPanel(debug, player, caps, stats, camera);

  let lastW = 0;
  let lastH = 0;
  let lastScale = 1;
  /** Keep renderer + camera in sync with the canvas (panes and tabs can resize without events). */
  const syncSize = (): boolean => {
    const w = engine.canvas.clientWidth;
    const h = engine.canvas.clientHeight;
    if (w === 0 || h === 0) return false;
    if (w !== lastW || h !== lastH || dynamicResolution.scale !== lastScale) {
      lastW = w;
      lastH = h;
      lastScale = dynamicResolution.scale;
      applyRendererSize(renderer, engine.canvas);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    return true;
  };

  // Attract mode: until the player first clicks in, the camera hangs above the start
  // point and slowly turns, so the start screen shows off the world. ?attract=0 disables.
  let attract =
    flags.bool('attract', true) &&
    !flags.has('cam') &&
    (world instanceof HollowScene || world instanceof CaveWorld);
  let ambienceDef = world.ambience;
  let ambience = new Ambience(ambienceDef ?? { wind: { gain: 0.6, cutoff: 500 } });
  const attractStart = performance.now();
  // How high it hangs: 45 m, or less in a small world (from 45 m up in Nexus, 400 m across,
  // the view was an aerial photo of a meadow rather than the inside of a world).
  const attractLift = Math.min(45, 0.06 * (world.hollowAt(player.position)?.radius ?? 1e4));
  // Phones and tablets: on-screen controls instead of pointer lock.
  const touch = touchDevice ? new TouchControls(engine.input) : null;
  if (touch) document.querySelector('#play .brand-tag')!.textContent = 'Tap to enter';
  const startPlaying = () => (touch ? engine.input.setTouchActive(true) : engine.input.requestPointerLock());
  engine.input.onLockChange = (locked) => {
    screens.setPlaying(locked);
    touch?.setVisible(locked);
    player.controlsEnabled = locked;
    if (locked) attract = false;
    // Sound can only start from a user gesture: the click that captures the mouse.
    if (locked && !ambience.started) {
      ambience.start(settings.muted);
      showHintsOnce(sceneName, world.hints);
    }
  };
  screens.onPlayClick(startPlaying);
  setupStartScreen(sceneName, startPlaying);
  player.controlsEnabled = false;
  // Keep the boot screen up while the shaders compile (materials revealed one per frame,
  // their pipelines compiling in the background), then until the terrain around the camera
  // has streamed in. Other hollows' shaders warm up after that, while you play.
  const warmup = new ShaderWarmup([scene, lightScene]);
  let streamStart = -1;
  let booted = false;
  let bootedAt = 0;
  // Frames in a row with no shader compiling: a revealed object asks for its pipeline when
  // it is next drawn, so one quiet frame isn't proof that nothing more is coming.
  let quietFrames = 0;
  engine.add({
    name: 'boot',
    frameUpdate: () => {
      if (booted) return;
      quietFrames = warmup.done && pipelines.pending === 0 ? quietFrames + 1 : 0;
      if (quietFrames < 5) {
        warmup.step();
        const compiled = pipelines.started > 0 ? pipelines.finished / pipelines.started : 0;
        const progress = 0.5 * warmup.progress + 0.5 * (warmup.done ? compiled : 0);
        screens.bootStatus(`Preparing shaders… ${Math.round(progress * 100)}%`, 0.3 + 0.55 * progress);
        return;
      }
      if (streamStart < 0) {
        streamStart = performance.now();
        markShadersReady(sceneName);
        console.info('[innerworlds] boot: shaders ready', since());
      }
      const waited = performance.now() - streamStart;
      const progress = world.loadProgress?.();
      if (progress !== undefined) {
        screens.bootStatus(
          `Growing the far side of the world… ${Math.round(progress * 100)}%`,
          0.85 + 0.15 * progress,
        );
      }
      if ((world.settled?.() ?? true) || waited > 8000) {
        booted = true;
        bootedAt = performance.now();
        console.info(
          '[innerworlds] boot: ready',
          since(),
          world.settled?.() === false ? '(still streaming)' : '',
        );
        screens.hideBoot();
        screens.setPlaying(engine.input.pointerLocked);
        world.warmInBackground?.();
      }
    },
  });

  // ---- simulation order: player → world colliders → actions → physics ----
  engine.add(player);
  engine.add({ name: 'world', fixedUpdate: () => world.update(player) });
  engine.add({
    name: 'actions',
    fixedUpdate: () => {
      const input = engine.input;
      if (!player.controlsEnabled) return;
      if (input.wasPressed('KeyR')) void world.spawn(player);
      if (input.wasPressed('KeyL')) world.toggleLantern?.();
      if (input.wasPressed('KeyO')) {
        shoulder.enabled = !shoulder.enabled;
        settings.thirdPerson = shoulder.enabled;
        saveSettings();
      }
      if (input.wasPressed('KeyM')) {
        settings.muted = !settings.muted;
        saveSettings();
        ambience.setMuted(settings.muted);
      }
      if (world.rig) world.rig.timeScale = input.isDown('KeyT') ? 90 : 1;
      // (With the combat test on, the left button attacks; F still throws.)
      if (input.wasPressed('KeyF') || (!combat && input.wasClicked(0))) {
        player.cameraPose(1, _eye, _viewQ);
        _fwd.set(0, 0, -1).applyQuaternion(_viewQ);
        stones.throw(_tmp.copy(_eye).addScaledVector(_fwd, 0.6), _fwd, player.velocity);
      }
    },
  });
  if (combat) engine.add({ name: 'combat', fixedUpdate: (dt) => combat.fixedUpdate(dt) });
  engine.add({ name: 'physics', fixedUpdate: (dt) => physics.step(dt) });

  // Dynamic resolution keeps slower GPUs near 45+ fps. ?dynres=0 turns it off.
  if (flags.bool('dynres', true)) {
    engine.add(
      new DynamicResolution({
        slowMs: flags.num('dynresMs', 24),
        canMeasure: () => booted && performance.now() - bootedAt > 4000,
        onChange: (s) => stats.set('resolution', `${Math.round(s * 100)}% (dynamic)`),
      }),
    );
  }

  // ---- presentation ----
  let alpha = 0;
  engine.add({
    name: 'present',
    frameUpdate: (dt, a) => {
      alpha = a;
      // Ambient sound follows the local time of day (0 night … 1 day).
      const home = world.hollowAt(player.position);
      const day =
        world.rig && home
          ? world.rig.veilAtCpu(_tmp.subVectors(player.position, home.center).normalize())
          : 1;
      // Walking into another world (through a cave) changes the soundscape.
      if (ambience.started && world.ambience && world.ambience !== ambienceDef) {
        ambience.stop();
        ambienceDef = world.ambience;
        ambience = new Ambience(ambienceDef);
        ambience.start(settings.muted);
      }
      // A cave's echo follows the size of the space you're in.
      if (world.echo) {
        const e = world.echo();
        ambience.setEcho(e?.amount ?? ambienceDef?.echo ?? 0, e?.delay ?? 0.27);
      }
      ambience.update(dt, day);
    },
  });

  // Handle for automated testing and console poking.
  const shot = (name: string) => saveScreenshot(renderer, engine.canvas, name, () => engine.render?.());
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyH') document.body.classList.toggle('clean');
    if (e.code === 'KeyP') {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      void shot(`photo-${stamp}`).then((m) => console.info('[innerworlds]', m));
    }
  });

  const tour = async (externalShot?: (name: string) => Promise<string>) => {
    if (!(world instanceof HollowScene)) return ['tour needs a terrain hollow (not the sandbox)'];
    attract = false;
    const { runPhotoTour } = await import('./dev/photoTour');
    return runPhotoTour({ engine, player, world, shot: externalShot ?? shot });
  };

  (window as unknown as Record<string, unknown>).__iw = {
    shot,
    tour,
    engine,
    player,
    world,
    physics,
    origin,
    stones,
    combat,
    hero,
    shoulder,
    camera,
    scene,
    stats,
    ambience,
    THREE,
    setAttract: (on: boolean) => {
      attract = on;
    },
  };

  engine.render = () => {
    if (!syncSize()) return;
    const t0 = performance.now();
    player.cameraPose(alpha, _eye, _viewQ);
    if (attract) {
      // Above the start point, turning slowly, looking a little up at the world.
      const t = (performance.now() - attractStart) / 1000;
      _eye.copy(player.position).addScaledVector(player.up, attractLift * (1 + 0.13 * Math.sin(t * 0.13)));
      _viewQ
        .copy(player.body)
        .multiply(_attractQ.setFromAxisAngle(_Y, t * 0.035))
        .multiply(_attractQ2.setFromAxisAngle(_X, 0.12 + 0.05 * Math.sin(t * 0.07)));
    }
    // Over the shoulder the camera backs away from the eye (not while it shows off the world).
    if (attract) _cam.copy(_eye);
    else shoulder.place(_eye, _viewQ, engine.frameDt, _cam);
    combat?.shakeView(_viewQ, engine.frameDt);
    camera.position.set(0, 0, 0);
    camera.quaternion.copy(_viewQ);
    origin.update(_cam);
    // Combat first: it puts the weapon in the hero's hands for this frame.
    combat?.frame(
      engine.frameDt,
      camera,
      _viewQ,
      !shoulder.showsHero,
      booted && !attract && !document.getElementById('hud')?.classList.contains('hidden'),
      !booted,
    );
    // The hero shows once the camera is out of its head (and while booting, to compile it),
    // lit by the light around it and its lantern, which then hangs at its hip. Its lights are
    // kept up in first person too: the weapon in view uses them.
    const showHero = !booted || shoulder.showsHero;
    hero.group.visible = showHero;
    hero.lanternLit = world.lanternLit ?? false;
    if (world.heroLight) world.heroLight(player.feet(_tmp), hero.ambient);
    else hero.ambient.setRGB(0.3, 0.3, 0.3);
    if (world.heroSun) world.heroSun(player.feet(_tmp), hero.sunDirection, hero.sun);
    else hero.sun.setRGB(0, 0, 0);
    hero.update(player, alpha, engine.frameDt, _cam);
    world.holdLantern?.(booted && showHero ? hero.lanternRender : null);
    world.frame(camera, _cam, engine.frameDt);
    if (world.currentExposure !== undefined) renderer.toneMappingExposure = world.currentExposure;

    const aiming = player.controlsEnabled && engine.input.isButtonDown(2);
    stones.setPreviewVisible(aiming);
    if (aiming) {
      _fwd.set(0, 0, -1).applyQuaternion(_viewQ);
      stones.preview(world.field, _tmp.copy(_eye).addScaledVector(_fwd, 0.6), _fwd, player.velocity, (p) =>
        isSolid(world, p),
      );
    }
    stones.frame();

    _invQ.copy(_viewQ).invert();
    _gView.copy(player.gravity).applyQuaternion(_invQ);
    hud.update(_gView, world.loadingNote?.() ?? describeLocation(world, player), player.mode);
    if (world.waypoints) {
      // Cave mouths on screen: in front of the camera, projected; behind it, a direction
      // pushed past the edge (the HUD pins those to the edge of the view).
      const tanY = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
      const tanX = tanY * camera.aspect;
      hud.markers(
        world.waypoints(player.position).map((w) => {
          _tmp.subVectors(w.point, _cam).applyQuaternion(_invQ);
          if (_tmp.z < -0.5)
            return { x: _tmp.x / -_tmp.z / tanX, y: _tmp.y / -_tmp.z / tanY, label: w.label };
          const l = Math.hypot(_tmp.x, _tmp.y);
          return l > 1e-6
            ? { x: (_tmp.x / l) * 3, y: (_tmp.y / l) * 3, label: w.label }
            : { x: 0, y: -3, label: w.label };
        }),
      );
    }

    const post = postFor(world.atmosphere, world.clouds);
    if (world.viewRadius) surface.fitToWorld(world.viewRadius);
    // While booting, draw every other sky too, so its shaders compile along with this one's.
    // After that, a sky that hasn't been drawn for a few seconds gives back its targets.
    const now = performance.now();
    for (const p of posts.values()) {
      if (p === post) continue;
      if (!booted) p.render();
      else if (now - p.lastUsed > 3000) p.release();
    }
    post.render();
    const extra = world.stats?.();
    if (extra) for (const k in extra) stats.set(k, extra[k]);
    stats.frame(performance.now() - t0);
  };
}

/** Is a universe position inside rock or ground (for trajectory previews)? */
function isSolid(world: GameWorld, p: THREE.Vector3): boolean {
  if (world.solidAt?.(p) === false) return false;
  const h = world.hollowAt(p);
  if (!h) return true;
  const ground = world.groundAltitude?.(p);
  const alt = world.heightAbove?.(p) ?? h.radius - p.distanceTo(h.center);
  return alt < (ground ?? 0);
}

function describeLocation(world: GameWorld, player: PlayerController): string {
  const feet = player.feet(_tmp);
  const h = world.hollowAt(feet);
  if (h) {
    const ground = world.groundAltitude?.(feet) ?? 0;
    const r = feet.distanceTo(h.center);
    const alt = (world.heightAbove?.(feet) ?? h.radius - r) - ground;
    const time = world.describeTime ? ` · ${world.describeTime(feet)}` : '';
    const guide = world.guide?.(feet);
    const shape = world.describeShape?.(feet);
    const size = shape?.size ?? `r ${(h.radius / 1000).toFixed(1)} km`;
    const middle = shape?.middle ?? `${(r / 1000).toFixed(2)} km to the ${h.sun ? 'sun' : 'centre'}`;
    return `${h.name} · ${size} · ${alt.toFixed(1)} m above ground · ${middle}${time}${guide ? ` · ${guide}` : ''}`;
  }
  const cave = world.describeCave?.(feet);
  if (cave) return cave;
  let best = world.hollows[0];
  let bestD = Number.POSITIVE_INFINITY;
  for (const hh of world.hollows) {
    const d = feet.distanceTo(hh.center) - hh.radius;
    if (d < bestD) {
      bestD = d;
      best = hh;
    }
  }
  return `In the rock · ${bestD.toFixed(0)} m below ${best.name}`;
}
